import type { CSSProperties } from 'react'
import type { PlatformService } from '@/shared/api'
import { serviceColor } from '@/entities/platform'
import { cn } from '@/shared/lib/cn'
import { Icon, Popover, Skeleton, WidgetError } from '@/shared/ui'
import s from './PlatformSwitcher.module.css'

type PlatformSwitcherProps = {
  services: PlatformService[] | undefined
  error: unknown
  onRetry: () => void
}

/**
 * Свитчер сервисов Prima (prototype/Prototype.dc.html:119-123, поповер — 246-282).
 * Текущий сервис — своя карточка сверху («вы здесь»), остальные — сетка 2×2.
 *
 * Явная кнопка-крестик закрытия (строка 251) — не костыль макета: в отличие от `fixed
 * inset-0`-оверлея (Escape/клик вне уже даёт Radix), это законный второй способ закрыть,
 * который в макете нарисован. `Popover.Close` закрывает и без контролируемого `open` —
 * своего состояния этому поповеру не нужно (в отличие от свитчера компаний и уведомлений,
 * у которых открытие вызывает побочный эффект).
 *
 * Сервис без `url` показываем неактивной плиткой, а не ссылкой в никуда: `PlatformService.url`
 * необязателен в контракте (`openapi/components/schemas/platform.yaml`, `url` не в `required`),
 * и `<a>` без `href` выглядел бы кликабельным (курсор, ховер), но молча ничего не делал.
 * Тот же принцип, что у ссылок «Сервис «Компании»»/«Личный кабинет» и у `pages/no-companies`:
 * настоящего адреса нет — значит нет и перехода.
 */
export function PlatformSwitcher({ services, error, onRetry }: PlatformSwitcherProps) {
  const current = services?.find((service) => service.current)
  const others = services?.filter((service) => !service.current) ?? []

  return (
    <Popover
      align="start"
      width={440}
      trigger={
        <button type="button" className={s.trigger} title="Сервисы платформы Prima">
          {/*
           * Плитка «P» и надпись «prima бронирования» — ВОРДМАРК самого приложения, а не данные
           * с сервера, и хардкод здесь намеренный. Ревью уже поднимало этот вопрос; ответ
           * записан тут, чтобы не открывать его заново.
           *
           * Подставить сюда `current.name` нельзя — это СЛОМАЕТ соответствие макету. В макете
           * (строка 121) надпись набрана «prima бронирования» со СТРОЧНОЙ буквы, как хвостик
           * логотипа. А `PlatformService.name` — это «Бронирования» с прописной, название
           * сервиса в списке. Из `current.name` вышло бы «prima Бронирования».
           *
           * Внутри поповера, где сервис показан как элемент данных, всё честно берётся с
           * сервера — и `name`, и `slug`, и `initials` (см. ниже). Разница ровно в том, что
           * снаружи это логотип, а внутри — запись в списке. Того же класса `<title>` страницы
           * и заголовок на экране входа.
           */}
          <span className={s.logo}>P</span>
          <span className={s.wordmark}>
            <b>prima</b> <span className={s.wordmarkSub}>бронирования</span>
          </span>
          <Icon name="chevron-down" size={11} strokeWidth={2.4} className={s.chevron} />
        </button>
      }
    >
      <div className={s.panel}>
        <div className={s.head}>
          <span className={s.label}>Платформа Prima</span>
          <Popover.Close asChild>
            <button type="button" title="Закрыть" className={s.close}>
              <Icon name="close" size={14} />
            </button>
          </Popover.Close>
        </div>

        {/* Данные — первыми, ошибка — только если показать нечего. Упавший фоновый перезапрос
            оставляет `data` целой и одновременно выставляет `error`; подробности — в TopBar. */}
        {!services ? (
          error ? (
            <WidgetError error={error} retry={onRetry} />
          ) : (
            <div className={s.skeletons}>
              <Skeleton width="100%" height={64} radius={12} />
              <Skeleton width="100%" height={56} radius={12} />
            </div>
          )
        ) : (
          <>
            {current && (
              <div className={s.current}>
                <span className={s.currentAvatar}>{current.initials}</span>
                <span className={s.currentText}>
                  <span className={s.name}>{current.name}</span>
                  <span className={s.slug}>{current.slug}</span>
                </span>
                <span className={s.here}>
                  <Icon name="check" size={15} strokeWidth={2.6} />
                  вы здесь
                </span>
              </div>
            )}

            {others.length > 0 && (
              <>
                <div className={s.otherLabel}>Другие сервисы</div>
                <div className={s.grid}>
                  {others.map((service, index) => {
                    const style = {
                      '--tile-color': serviceColor(service),
                      animationDelay: `${50 + index * 40}ms`,
                    } as CSSProperties

                    const body = (
                      <>
                        <span className={s.tileAvatar}>{service.initials}</span>
                        <span className={s.tileText}>
                          <span className={s.tileName}>{service.name}</span>
                          <span className={s.tileSlug}>{service.slug}</span>
                        </span>
                        {service.url && (
                          <Icon name="chevron-right" size={14} className={s.tileChevron} />
                        )}
                      </>
                    )

                    return service.url ? (
                      <a key={service.key} href={service.url} className={s.tile} style={style}>
                        {body}
                      </a>
                    ) : (
                      <div
                        key={service.key}
                        className={cn(s.tile, s.tileInert)}
                        style={style}
                        title="Адрес сервиса не настроен"
                      >
                        {body}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Popover>
  )
}
